import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	checkKaiAssistantName,
	createKaiAssistant as createKaiAssistantApi,
	deleteKaiAssistant as removeKaiAssistantApi,
	disableKaiAssistant as disableKaiAssistantApi,
	disableKaiAssistants as disableKaiAssistantsApi,
	enableKaiAssistant as enableKaiAssistantApi,
	enableKaiAssistants as enableKaiAssistantsApi,
	hideKaiAssistant as hideKaiAssistantApi,
	setDefaultKaiAssistant as setDefaultKaiAssistantApi,
	setNonDefaultKaiAssistants as setNonDefaultKaiAssistantsApi,
	unhideKaiAssistant as unhideKaiAssistantApi,
	updateKaiAssistant as updateKaiAssistantApi,
} from '@api/kaiAssistants';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IResponseError } from '@interfaces/errors/apiErrors';
import { ICheckKaiAssistantNameDTO, ICreateKaiAssistantDTO, IUpdateKaiAssistantDTO } from '@interfaces/kai/dto/kaiDTO';

import { ESnackbarTypes, EUserRoles } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import {
	CHECK_KAI_ASSISTANT_NAME,
	CREATE_KAI_ASSISTANT,
	DISABLE_KAI_ASSISTANTS,
	ENABLE_KAI_ASSISTANTS,
	GET_KAI_ASSISTANTS,
	HIDE_KAI_ASSISTANTS,
	ORGANISATION_PROFILE,
	REMOVE_KAI_ASSISTANT,
	SET_DEFAULT_KAI_ASSISTANT,
	SET_NON_DEFAULT_KAI_ASSISTANT,
	SETTINGS,
	UNHIDE_KAI_ASSISTANTS,
	UPDATE_KAI_ASSISTANT,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export interface IMutateKaiAssistantsReturned {
	createKaiAssistant: (data: ICreateKaiAssistantDTO) => Promise<void>;
	isCreatingKaiAssistant: boolean;
	enableKaiAssistant: (id: string) => Promise<void>;
	isEnablingKaiAssistant: boolean;
	disableKaiAssistant: (id: string) => Promise<void>;
	isDisablingKaiAssistant: boolean;
	deleteKaiAssistant: (id: string) => Promise<void>;
	isDeletingKaiAssistant: boolean;
	updateKaiAssistant: (data: IUpdateKaiAssistantDTO) => Promise<void>;
	isUpdatingKaiAssistant: boolean;
	enableKaiAssistants: (id: string) => Promise<void>;
	isEnablingKaiAssistants: boolean;
	disableKaiAssistants: (id: string) => Promise<void>;
	isDisablingKaiAssistants: boolean;
	setDefaultKaiAssistant: (id: string) => Promise<void>;
	isSettingDefaultKaiAssistant: boolean;
	setNonDefaultKaiAssistant: (id: string) => Promise<void>;
	isSettingNonDefaultKaiAssistant: boolean;
	handleCheckKaiAssistantName: (data: ICheckKaiAssistantNameDTO) => Promise<void>;
	isLoadingCheckKaiAssistantName: boolean;
	hideKaiAssistant: (id: string) => Promise<void>;
	isHidingKaiAssistant: boolean;
	unhideKaiAssistant: (id: string) => Promise<void>;
	isUnhidingKaiAssistant: boolean;
}

export function useMutateKaiAssistants(): IMutateKaiAssistantsReturned {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		mutateAsync: createKaiAssistant,
		isLoading: isCreatingKaiAssistant,
	} = useMutation<ICreateKaiAssistantDTO>({
		mutationFn: createKaiAssistantApi,
		mutationKey: [CREATE_KAI_ASSISTANT],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.createKaiAssistantSuccess,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_KAI_ASSISTANTS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.createKaiAssistantError,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: enableKaiAssistant,
		isLoading: isEnablingKaiAssistant,
	} = useMutation<string>({
		mutationFn: enableKaiAssistantApi,
		mutationKey: [CREATE_KAI_ASSISTANT],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.enableKaiAssistantSuccess,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_KAI_ASSISTANTS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.enableKaiAssistantError,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: disableKaiAssistant,
		isLoading: isDisablingKaiAssistant,
	} = useMutation<string>({
		mutationFn: disableKaiAssistantApi,
		mutationKey: [CREATE_KAI_ASSISTANT],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.disableKaiAssistantSuccess,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_KAI_ASSISTANTS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.disableKaiAssistantError,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: hideKaiAssistant,
		isLoading: isHidingKaiAssistant,
	} = useMutation<string>({
		mutationFn: hideKaiAssistantApi,
		mutationKey: [HIDE_KAI_ASSISTANTS],
		allowedRoles: [EUserRoles.platformOwnerAdministrator],
		onSuccess: () => {
			return [
				queryClient.invalidateQueries([ORGANISATION_PROFILE]),
				queryClient.invalidateQueries([SETTINGS]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: unhideKaiAssistant,
		isLoading: isUnhidingKaiAssistant,
	} = useMutation<string>({
		mutationFn: unhideKaiAssistantApi,
		mutationKey: [UNHIDE_KAI_ASSISTANTS],
		allowedRoles: [EUserRoles.platformOwnerAdministrator],
		onSuccess: () => {
			return [
				queryClient.invalidateQueries([ORGANISATION_PROFILE]),
				queryClient.invalidateQueries([SETTINGS]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: updateKaiAssistant,
		isLoading: isUpdatingKaiAssistant,
	} = useMutation<IUpdateKaiAssistantDTO>({
		mutationFn: updateKaiAssistantApi,
		mutationKey: [UPDATE_KAI_ASSISTANT],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updateKaiAssistantSuccess,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_KAI_ASSISTANTS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.updateKaiAssistantError,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: deleteKaiAssistant,
		isLoading: isDeletingKaiAssistant,
	} = useMutation<string>({
		mutationFn: removeKaiAssistantApi,
		mutationKey: [REMOVE_KAI_ASSISTANT],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.removeKaiAssistantSuccess,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_KAI_ASSISTANTS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.removeKaiAssistantError,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: setDefaultKaiAssistant,
		isLoading: isSettingDefaultKaiAssistant,
	} = useMutation<string>({
		mutationFn: setDefaultKaiAssistantApi,
		mutationKey: [SET_DEFAULT_KAI_ASSISTANT],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.setDefaultKaiAssistant,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_KAI_ASSISTANTS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: setNonDefaultKaiAssistant,
		isLoading: isSettingNonDefaultKaiAssistant,
	} = useMutation<string>({
		mutationFn: setNonDefaultKaiAssistantsApi,
		mutationKey: [SET_NON_DEFAULT_KAI_ASSISTANT],
		onSuccess: () => {
			return queryClient.invalidateQueries([GET_KAI_ASSISTANTS]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: enableKaiAssistants,
		isLoading: isEnablingKaiAssistants,
	} = useMutation<string>({
		mutationFn: enableKaiAssistantsApi,
		mutationKey: [ENABLE_KAI_ASSISTANTS],
		onSuccess: () => {
			return [
				queryClient.invalidateQueries([ORGANISATION_PROFILE]),
				queryClient.invalidateQueries([SETTINGS]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: disableKaiAssistants,
		isLoading: isDisablingKaiAssistants,
	} = useMutation<string>({
		mutationFn: disableKaiAssistantsApi,
		mutationKey: [DISABLE_KAI_ASSISTANTS],
		onSuccess: () => {
			return [
				queryClient.invalidateQueries([ORGANISATION_PROFILE]),
				queryClient.invalidateQueries([SETTINGS]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: handleCheckKaiAssistantName,
		isLoading: isLoadingCheckKaiAssistantName,
	} = useMutation<ICheckKaiAssistantNameDTO>({
		mutationKey: [CHECK_KAI_ASSISTANT_NAME],
		mutationFn: checkKaiAssistantName,
		onError: (err: IResponseError) => {
			if (!err.errors.find((item) => item.code === EErrorCodes.kaiAssistantNameMustBeUnique)) {
				handleErrorFromMessage(err);
			}
		},
	});

	return {
		createKaiAssistant,
		isCreatingKaiAssistant,
		enableKaiAssistant,
		isEnablingKaiAssistant,
		disableKaiAssistant,
		isDisablingKaiAssistant,
		deleteKaiAssistant,
		isDeletingKaiAssistant,
		updateKaiAssistant,
		isUpdatingKaiAssistant,
		enableKaiAssistants,
		isEnablingKaiAssistants,
		disableKaiAssistants,
		isDisablingKaiAssistants,
		setDefaultKaiAssistant,
		isSettingDefaultKaiAssistant,
		setNonDefaultKaiAssistant,
		isSettingNonDefaultKaiAssistant,
		handleCheckKaiAssistantName,
		isLoadingCheckKaiAssistantName,
		hideKaiAssistant,
		isHidingKaiAssistant,
		unhideKaiAssistant,
		isUnhidingKaiAssistant,
	};
}
